import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import config from '../config';
import DatabaseInspector from '../components/DatabaseInspector';
import AdvancedSearchModal from '../components/AdvancedSearchModal';
import statusCodes from '../data/statusCodes';
import './Pipeline.css';

const PHASE_NAMES = [
  'Phase 0: Ingestion',
  'Phase 1: Embeddings & Clustering',
  'Phase 2: Claim Selection',
  'Phase 3: Feature Extraction',
  'Phase 4: Technical Digests',
  'Phase 5: Semantic Labels',
  'Phase 6: Catalogue Drafts',
  'Phase 7: Validation',
  'Phase 8: Portfolio Navigation'
];

const SELECTABLE_PHASES = PHASE_NAMES.slice(1).map((name, index) => ({
  id: index + 1,
  name
}));

const normalizePatentId = (value = '') => value.replace(/[^0-9]/g, '');

const parsePatentInput = (text) => {
  if (!text) return [];
  const tokens = text.split(/\s|,|;|\n/);
  const seen = new Set();
  const parsed = [];
  tokens.forEach((token) => {
    const normalized = normalizePatentId(token);
    if (normalized && normalized.length >= 7 && !seen.has(normalized)) {
      seen.add(normalized);
      parsed.push(normalized);
    }
  });
  return parsed;
};

// Check if an application is alive (downloadable) based on status code
const isApplicationAlive = (statusCode) => {
  if (!statusCode) return true; // If no status code, assume alive
  const statusInfo = statusCodes.find(s => s.code === parseInt(statusCode));
  return statusInfo ? statusInfo.alive : true; // Default to alive if not found
};

function Pipeline() {
  const [activeTab, setActiveTab] = useState('pto');

  // Tab 1: PTO Download state
  const [ptoInput, setPtoInput] = useState('');
  const [ptoPatents, setPtoPatents] = useState([]);
  const [ptoSource, setPtoSource] = useState('USPTO');
  const [ptoDownloading, setDownloading] = useState(false);
  const [ptoStatus, setPtoStatus] = useState({ status: 'idle', log: [] });
  const [ptoError, setPtoError] = useState(null);
  const [ptoMessage, setPtoMessage] = useState(null);
  const [ptoNotebookTab, setPtoNotebookTab] = useState('number');

  const [searchProvider, setSearchProvider] = useState('USPTO');
  const [searchResults, setSearchResults] = useState([]);
  const [searchSelected, setSearchSelected] = useState([]);
  const [existingPatents, setExistingPatents] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searchMessage, setSearchMessage] = useState(null);
  const [searchPage, setSearchPage] = useState(0);
  const [searchResultsPerPage, setSearchResultsPerPage] = useState(25);
  const [searchFacets, setSearchFacets] = useState(null);
  const [searchVisibleColumns, setSearchVisibleColumns] = useState(['application_number', 'title', 'application_date', 'status', 'pdf_link']);
  const [columnSelectorOpen, setColumnSelectorOpen] = useState(false);
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
  const [advancedSearchQuery, setAdvancedSearchQuery] = useState('');
  const [advancedSearchCriteria, setAdvancedSearchCriteria] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Tab 2: Processing state
  const [selectedPhases, setSelectedPhases] = useState([]);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [processingError, setProcessingError] = useState(null);
  const [processingMessage, setProcessingMessage] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Tab 3: Database state
  const [dbStats, setDbStats] = useState(null);
  const [dbLoading, setDbLoading] = useState(false);

  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Server restart
  const [restarting, setRestarting] = useState(false);
  const [restartError, setRestartError] = useState(null);

  const formatTimestamp = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleString();
  };

  // Available columns for search results
  const availableColumns = [
    { key: 'patent_number', label: 'Patent Number' },
    { key: 'publication_number', label: 'Publication Number' },
    { key: 'application_number', label: 'Application Number' },
    { key: 'pdf_link', label: 'PDF Link' },
    { key: 'title', label: 'Title' },
    { key: 'patentee', label: 'Patentee/Assignee' },
    { key: 'inventor_names', label: 'Inventors' },
    { key: 'application_type', label: 'Application Type' },
    { key: 'status', label: 'Application Status' },
    { key: 'status_code', label: 'Status Code' },
    { key: 'art_unit', label: 'Art Unit' },
    { key: 'application_date', label: 'Filing Date' },
    { key: 'grant_date', label: 'Grant Date' },
    { key: 'attorney_docket', label: 'Attorney Docket' },
    { key: 'classification_cpc', label: 'CPC Class' }
  ];

  const toggleColumn = (columnKey) => {
    setSearchVisibleColumns(prev => {
      if (prev.includes(columnKey)) {
        // Keep at least one column visible
        if (prev.length === 1) return prev;
        return prev.filter(k => k !== columnKey);
      }
      return [...prev, columnKey];
    });
  };

  const handleSort = (columnKey) => {
    if (columnKey === 'pdf_link') return; // Don't sort PDF links
    
    if (sortColumn === columnKey) {
      // Toggle direction
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const sortedResults = useMemo(() => {
    if (!sortColumn) return searchResults;
    
    const sorted = [...searchResults].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      
      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      
      // Compare values
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal, undefined, { numeric: true });
      }
      
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
      return 0;
    });
    
    return sortDirection === 'desc' ? sorted.reverse() : sorted;
  }, [searchResults, sortColumn, sortDirection]);

  const readyCounts = useMemo(
    () => processingStatus?.ready_counts || {},
    [processingStatus]
  );
  const runStatus = processingStatus?.run_status || null;
  const progressInfo = useMemo(() => {
    if (!runStatus?.progress) {
      return { completed: 0, total: 0, percent: 0 };
    }
    const completed = runStatus.progress.completed || 0;
    const total = runStatus.progress.total || 0;
    const percent = total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0;
    return { completed, total, percent };
  }, [runStatus]);
  const phaseBreakdown = useMemo(() => {
    if (!runStatus?.progress) {
      return [];
    }
    const totals = runStatus.progress.per_phase_totals || {};
    const completed = runStatus.progress.per_phase_completed || {};
    const phaseKeys = new Set([
      ...Object.keys(totals || {}),
      ...Object.keys(completed || {})
    ]);
    return Array.from(phaseKeys)
      .map((key) => Number(key))
      .sort((a, b) => a - b)
      .map((phaseId) => {
        const dictKey = String(phaseId);
        return {
          phaseId,
          total: totals?.[dictKey] ?? 0,
          completed: completed?.[dictKey] ?? 0
        };
      });
  }, [runStatus]);
  const selectionBounds = useMemo(() => {
    if (!selectedPhases.length) {
      return null;
    }
    return {
      min: Math.min(...selectedPhases),
      max: Math.max(...selectedPhases)
    };
  }, [selectedPhases]);
  const batchJobs = useMemo(() => {
    if (!runStatus?.batches) {
      return [];
    }
    const getSortValue = (batch) => {
      const timestamp = batch?.submitted_at || batch?.completed_at;
      if (!timestamp) return 0;
      const parsed = new Date(timestamp).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    };
    return [...runStatus.batches].sort((a, b) => getSortValue(b) - getSortValue(a));
  }, [runStatus]);

  const availablePhaseStarts = useMemo(() => (
    SELECTABLE_PHASES.filter(phase => (readyCounts[phase.id] || 0) > 0).map(phase => phase.id)
  ), [readyCounts]);

  const selectedSearchPatentNumbers = useMemo(
    () => {
      const selected = searchResults.filter((result) => searchSelected.includes(result.result_id));
      console.log('Selected search results:', selected.map(r => ({
        result_id: r.result_id,
        application_number: r.application_number,
        patent_number: r.patent_number,
        publication_number: r.publication_number
      })));
      return selected
        .map((result) => result.application_number || result.patent_number || result.publication_number)
        .filter(Boolean);
    },
    [searchResults, searchSelected]
  );

  // Close column selector when clicking outside
  useEffect(() => {
    if (!columnSelectorOpen) return;
    
    const handleClickOutside = (e) => {
      if (!e.target.closest('.column-selector')) {
        setColumnSelectorOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [columnSelectorOpen]);

  // Parse PTO patent input
  useEffect(() => {
    setPtoPatents(parsePatentInput(ptoInput));
  }, [ptoInput]);

  useEffect(() => {
    if (!processingStatus || selectedPhases.length === 0) {
      return;
    }
    const startPhase = Math.min(...selectedPhases);
    const stillReady = processingStatus.ready_counts?.[startPhase] || 0;
    if (stillReady === 0) {
      setSelectedPhases([]);
    }
  }, [processingStatus, selectedPhases]);

  const refreshProcessingStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${config.apiBaseUrl}/pipeline/phases/status`);
      setProcessingStatus(data);
      setProcessingError(null);
    } catch (error) {
      console.error('Failed to load processing status', error);
      setProcessingError(error.response?.data?.error || 'Unable to load status');
    }
  }, []);

  const refreshDbStats = useCallback(async () => {
    setDbLoading(true);
    try {
      const { data } = await axios.get(`${config.apiBaseUrl}/pipeline/database/stats`);
      setDbStats(data);
    } catch (error) {
      console.error('Failed to load database stats', error);
    } finally {
      setDbLoading(false);
    }
  }, []);

  // ========== PTO DOWNLOAD FUNCTIONS ==========

  const refreshPtoStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${config.apiBaseUrl}/pipeline/pto/status`);
      let previousStatusValue = 'idle';

      setPtoStatus(prev => {
        previousStatusValue = prev.status;
        let nextStatus = { ...data };
        if (
          data.status === 'idle' &&
          (!data.log || data.log.length === 0) &&
          prev.log &&
          prev.log.length > 0
        ) {
          nextStatus = {
            ...data,
            log: prev.log,
            results: data.results || prev.results,
            current: data.current || prev.current
          };
        }
        return nextStatus;
      });

      if (data.status === 'completed' && previousStatusValue !== 'completed') {
        refreshDbStats();
        refreshProcessingStatus();
      }

      if (data.status === 'downloading') {
        setDownloading(true);
      } else if (data.status === 'completed' || data.status === 'error') {
        setDownloading(false);
      }
    } catch (error) {
      console.error('Failed to load PTO status', error);
    }
  }, [refreshDbStats, refreshProcessingStatus]);

  // Periodic refresh
  useEffect(() => {
    if (!autoRefresh) return undefined;

    const interval = setInterval(() => {
      if (activeTab === 'pto') {
        refreshPtoStatus();
      }
      if (activeTab === 'processing') {
        refreshProcessingStatus();
      }
      if (activeTab === 'database') {
        // Skip auto-refresh for database management
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [autoRefresh, activeTab, ptoDownloading, refreshPtoStatus, refreshProcessingStatus]);

  // React to tab changes
  useEffect(() => {
    if (activeTab === 'pto') {
      refreshPtoStatus();
    } else if (activeTab === 'processing') {
      refreshProcessingStatus();
    } else if (activeTab === 'database') {
      refreshDbStats();
    }
  }, [activeTab, refreshDbStats, refreshPtoStatus, refreshProcessingStatus]);

  // Load key stats on mount
  useEffect(() => {
    refreshDbStats();
    refreshPtoStatus();
    refreshProcessingStatus();
  }, [refreshDbStats, refreshPtoStatus, refreshProcessingStatus]);

  // Date range functions removed - now handled in advanced search modal

  const handleSearchSelect = (resultId) => {
    setSearchSelected((prev) => (
      prev.includes(resultId)
        ? prev.filter((id) => id !== resultId)
        : [...prev, resultId]
    ));
  };

  const handleSelectAllSearchResults = () => {
    // Only select alive (downloadable) applications that don't already exist
    setSearchSelected(
      searchResults
        .filter((result) => {
          const alive = isApplicationAlive(result.status_code);
          const identifier = result.application_number || result.patent_number || result.publication_number;
          const alreadyExists = existingPatents.includes(identifier);
          return alive && !alreadyExists;
        })
        .map((result) => result.result_id)
    );
  };

  const handleClearSearchSelection = () => {
    setSearchSelected([]);
  };

  const startPtoDownload = useCallback(async (patentNumbers, source) => {
    if (!patentNumbers || patentNumbers.length === 0) {
      setPtoError('No patent numbers provided');
      setSearchError('No patent numbers provided');
      return;
    }

    setDownloading(true);
    setPtoError(null);
    setPtoMessage(null);

    try {
      console.log('PTO Download - Sending patent_numbers:', patentNumbers);
      const { data } = await axios.post(`${config.apiBaseUrl}/pipeline/pto/download`, {
        patent_numbers: patentNumbers,
        source,
        async: true
      });

      setPtoMessage(data.message);
      setSearchMessage(`Download queued: ${data.message}`);
      setSearchError(null);
      refreshPtoStatus();
      
      // Switch to PTO tab to show download progress
      setActiveTab('pto');
    } catch (error) {
      console.error('PTO download failed', error);
      const errorMsg = error.response?.data?.error || 'Download request failed';
      setPtoError(errorMsg);
      setSearchError(errorMsg);
      setDownloading(false);
    }
  }, [refreshPtoStatus]);

  const handleSearchDownload = () => {
    if (selectedSearchPatentNumbers.length === 0) {
      setSearchError('Select at least one result to download');
      return;
    }

    console.log('Starting download for:', selectedSearchPatentNumbers);
    setSearchError(null);
    setSearchMessage(`Downloading ${selectedSearchPatentNumbers.length} patent(s)...`);
    startPtoDownload(selectedSearchPatentNumbers, searchProvider);
  };

  const handleAdvancedSearchApply = (criteria) => {
    setAdvancedSearchQuery(criteria.query);
    setAdvancedSearchCriteria(criteria);
    setAdvancedSearchOpen(false);
  };

  const handleExportToExcel = () => {
    if (searchResults.length === 0) return;
    
    // Get column labels
    const headers = searchVisibleColumns.map(colKey => {
      const col = availableColumns.find(c => c.key === colKey);
      return col ? col.label : colKey;
    });
    
    // Build CSV content
    const csvRows = [];
    csvRows.push(headers.join(','));
    
    searchResults.forEach(result => {
      const row = searchVisibleColumns.map(colKey => {
        let value = result[colKey];
        
        // Add descriptions for status_code and art_unit
        if (colKey === 'status_code' && result.status_code_description) {
          value = `${value} - ${result.status_code_description}`;
        } else if (colKey === 'art_unit' && result.art_unit_description) {
          value = `${value} - ${result.art_unit_description}`;
        }
        
        // Handle null/undefined
        if (value == null) return '';
        
        // Escape and quote values containing commas or quotes
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return '"' + stringValue.replace(/"/g, '""') + '"';
        }
        return stringValue;
      });
      csvRows.push(row.join(','));
    });
    
    // Create and download file
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `patent_search_results_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSearchPatents = async () => {
    setSearchLoading(true);
    setSearchError(null);
    setSearchMessage(null);
    setSearchPage(0);

    // Build filters, rangeFilters, and facets from advanced search criteria
    const filters = advancedSearchCriteria?.filters?.filter(f => f.field && f.value) || [];
    const rangeFilters = advancedSearchCriteria?.rangeFilters?.filter(rf => rf.field && (rf.from || rf.to)) || [];
    const facets = advancedSearchCriteria?.facets?.filter(f => f) || [];
    const status = advancedSearchCriteria?.status || null;

    const payload = {
      provider: searchProvider,
      status: status,
      advanced_query: advancedSearchQuery || null,
      filters: filters.length > 0 ? filters : null,
      rangeFilters: rangeFilters.length > 0 ? rangeFilters : null,
      facets: facets.length > 0 ? facets : null
    };

    try {
      const { data } = await axios.post(`${config.apiBaseUrl}/pipeline/pto/search`, payload);
      const normalizedResults = (data.results || []).map((result, index) => ({
        ...result,
        result_id: result.result_id || `${result.patent_number || result.application_number || 'result'}-${index}`
      }));
      setSearchResults(normalizedResults);
      setSearchSelected([]);
      setSearchFacets(data.facets || null);
      
      // Check which patents already exist in database
      if (normalizedResults.length > 0) {
        const identifiers = normalizedResults.map(r => 
          r.application_number || r.patent_number || r.publication_number
        ).filter(Boolean);
        
        if (identifiers.length > 0) {
          try {
            console.log('Checking existence for', identifiers.length, 'identifiers');
            const existsResponse = await axios.post(`${config.apiBaseUrl}/pipeline/pto/check-exists`, {
              identifiers: identifiers
            });
            console.log('Exists check response:', existsResponse.data);
            setExistingPatents(existsResponse.data.existing || []);
          } catch (err) {
            console.error('Failed to check existing patents:', err);
            setExistingPatents([]);
          }
        }
      }
      
      if (normalizedResults.length === 0) {
        setSearchMessage('No results found. Try adjusting your search criteria or using different keywords.');
      } else {
        setSearchMessage(data.message || `${normalizedResults.length} result${normalizedResults.length === 1 ? '' : 's'} found`);
      }
    } catch (error) {
      console.error('Patent search failed', error);
      setSearchError(error.response?.data?.error || 'Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  const handlePtoDownload = async () => {
    startPtoDownload(ptoPatents, ptoSource);
  };

  const handlePhaseToggle = (phaseId) => {
    setSelectedPhases(prev => {
      if (prev.includes(phaseId)) {
        return prev.filter(p => p < phaseId);
      }

      if (prev.length === 0) {
        return availablePhaseStarts.includes(phaseId) ? [phaseId] : prev;
      }

      const maxSelectedPhase = Math.max(...prev);
      if (phaseId === maxSelectedPhase + 1) {
        return [...prev, phaseId];
      }

      return prev;
    });
  };

  const getPhaseState = (phaseId) => {
    if (!processingStatus) {
      return {
        enabled: selectedPhases.includes(phaseId),
        reason: 'Loading phase status...'
      };
    }

    if (!selectionBounds) {
      const ready = readyCounts[phaseId] || 0;
      return {
        enabled: ready > 0,
        reason: ready > 0
          ? `${ready} pending patent${ready === 1 ? '' : 's'}`
          : 'No pending work for this phase'
      };
    }

    if (selectedPhases.includes(phaseId)) {
      return { enabled: true, reason: 'Click to remove this phase from the run' };
    }

    if (phaseId === selectionBounds.max + 1) {
      return { enabled: true, reason: 'Extend run to the next phase' };
    }

    if (phaseId < selectionBounds.min) {
      return { enabled: false, reason: 'Cannot start before the initial phase' };
    }

    return { enabled: false, reason: 'Select phases in sequence' };
  };

  const handleRunProcessing = async () => {
    if (selectedPhases.length === 0) {
      setProcessingError('No phases selected');
      return;
    }

    setProcessing(true);
    setProcessingError(null);
    setProcessingMessage(null);

    try {
      const phasesToRun = [...selectedPhases].sort((a, b) => a - b);
      const { data } = await axios.post(`${config.apiBaseUrl}/pipeline/phases/run`, {
        phases: phasesToRun
      });
      
      setProcessingMessage(
        data?.message || `Queued processing phases ${phasesToRun[0]}-${phasesToRun[phasesToRun.length - 1]}`
      );
      setSelectedPhases([]);
      refreshProcessingStatus();
      refreshDbStats();
    } catch (error) {
      console.error('Processing failed', error);
      setProcessingError(error.response?.data?.error || 'Failed to start processing');
    } finally {
      setProcessing(false);
    }
  };

  // ========== DATABASE FUNCTIONS ==========
  // Database Management tab is always accessible

  // ========== SERVER RESTART ==========

  const handleRestartServer = async () => {
    const confirmed = window.confirm(
      '⚠️ RESTART SERVER\n\n' +
      'This will restart the backend service and briefly interrupt all connections.\n\n' +
      'The page will automatically reconnect after restart.\n\n' +
      'Are you sure you want to continue?'
    );

    if (!confirmed) return;

    setRestarting(true);
    setRestartError(null);

    try {
      await axios.post(`${config.apiBaseUrl}/admin/restart`);
      
      // Wait a few seconds for the service to restart
      setTimeout(() => {
        window.location.reload();
      }, 5000);
    } catch (error) {
      console.error('Failed to restart server', error);
      setRestartError(error.response?.data?.error || 'Failed to restart server');
      setRestarting(false);
    }
  };

  // ========== RENDER ==========

  return (
    <div className="pipeline-page">
      <section className="card pipeline-hero">
        <div>
          <p className="eyebrow">Pipeline Control</p>
          <h1>Patent Data Acquisition & Processing</h1>
          <p className="subtitle">
            Download patents from USPTO, process through analysis pipeline, and manage the database.
          </p>
        </div>
        <div className="hero-stats">
          <div>
            <span>Auto-refresh</span>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              <span className="slider"></span>
            </label>
          </div>
          <div>
            <button
              className="btn btn-warning"
              onClick={handleRestartServer}
              disabled={restarting}
              title="Restart the backend service"
            >
              {restarting ? '🔄 Restarting...' : '🔄 Restart Server'}
            </button>
            {restartError && <div className="inline-error" style={{marginTop: '8px'}}>{restartError}</div>}
          </div>
        </div>
      </section>

      <div className="pipeline-tabs">
        <button
          className={`tab ${activeTab === 'pto' ? 'active' : ''}`}
          onClick={() => setActiveTab('pto')}
        >
          📥 PTO Download
        </button>
        <button
          className={`tab ${activeTab === 'processing' ? 'active' : ''}`}
          onClick={() => setActiveTab('processing')}
        >
          ⚙️ Processing
        </button>
        <button
          className={`tab ${activeTab === 'database' ? 'active' : ''}`}
          onClick={() => setActiveTab('database')}
        >
          🗄️ Database Management
        </button>
      </div>

      {/* TAB 1: PTO DOWNLOAD */}
      {activeTab === 'pto' && (
        <div className="tab-content">
          <section className="card pipeline-card">
            <header>
              <div>
                <h2>Download Patents from PTO</h2>
                <p>Use the notebook to queue downloads by number or discover new filings via search.</p>
              </div>
            </header>

            <div className="pto-notebook">
              <div className="pto-notebook-tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  className={ptoNotebookTab === 'number' ? 'subtab active' : 'subtab'}
                  onClick={() => setPtoNotebookTab('number')}
                >
                  📋 By Number
                </button>
                <button
                  type="button"
                  role="tab"
                  className={ptoNotebookTab === 'search' ? 'subtab active' : 'subtab'}
                  onClick={() => setPtoNotebookTab('search')}
                >
                  🔎 Search
                </button>
              </div>
              <div className="pto-notebook-content">
                {ptoNotebookTab === 'number' && (
                  <div className="form-section" role="tabpanel">
                    <div className="form-row">
                      <label>Source</label>
                      <select value={ptoSource} onChange={(e) => setPtoSource(e.target.value)}>
                        <option value="USPTO">USPTO (United States)</option>
                        <option value="EPO" disabled>EPO (European) - Coming Soon</option>
                        <option value="JPO" disabled>JPO (Japan) - Coming Soon</option>
                      </select>
                    </div>

                    <div className="form-row">
                      <label>Patent Numbers</label>
                      <textarea
                        className="patent-textarea"
                        rows={8}
                        placeholder="Enter patent numbers (one per line or comma-separated)&#10;Examples:&#10;12345678&#10;16123456, 16234567&#10;US20230123456"
                        value={ptoInput}
                        onChange={(e) => setPtoInput(e.target.value)}
                        disabled={ptoDownloading}
                      />
                      <div className="textarea-footer">
                        <span>{ptoPatents.length} patent numbers ready</span>
                        {ptoPatents.length > 0 && (
                          <span className="preview">
                            Preview: {ptoPatents.slice(0, 5).join(', ')}
                            {ptoPatents.length > 5 && ` ... +${ptoPatents.length - 5} more`}
                          </span>
                        )}
                      </div>
                    </div>

                    <button
                      className="btn btn-primary"
                      onClick={handlePtoDownload}
                      disabled={ptoPatents.length === 0 || ptoDownloading}
                    >
                      {ptoDownloading ? 'Downloading...' : `Download ${ptoPatents.length} Patents`}
                    </button>
                  </div>
                )}

                {ptoNotebookTab === 'search' && (
                  <div className="form-section search-form" role="tabpanel">
                      <div className="form-row">
                      <label>Provider</label>
                      <select value={searchProvider} onChange={(e) => setSearchProvider(e.target.value)}>
                        <option value="USPTO">USPTO (live)</option>
                        <option value="EPO">EPO (coming soon)</option>
                      </select>
                    </div>

                    <div className="advanced-search-section">
                      <button
                        className="btn btn-secondary"
                        onClick={() => setAdvancedSearchOpen(true)}
                        type="button"
                      >
                        Advanced Search Criteria
                      </button>
                      {advancedSearchQuery && (
                        <div className="query-display">
                          <label>Generated Query:</label>
                          <textarea
                            readOnly
                            value={advancedSearchQuery}
                            rows={3}
                            className="query-textarea"
                          />
                        </div>
                      )}
                    </div>

                    <div className="search-actions">
                      <button
                        className="btn btn-primary"
                        onClick={handleSearchPatents}
                        disabled={searchLoading}
                      >
                        {searchLoading ? 'Searching...' : 'Search Filings'}
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={handleSearchDownload}
                        disabled={selectedSearchPatentNumbers.length === 0 || ptoDownloading}
                      >
                        {ptoDownloading ? 'Queuing Download...' : `Download Selected (${selectedSearchPatentNumbers.length})`}
                      </button>
                    </div>

                    {searchMessage && !searchError && (
                      <div className="inline-success compact">{searchMessage}</div>
                    )}
                    {searchError && <div className="inline-error compact">{searchError}</div>}

                    <div className="search-results">
                      {searchLoading && (
                        <div className="search-loading">
                          <div className="loading-bar-container">
                            <div className="loading-bar"></div>
                          </div>
                          <div className="muted" style={{marginTop: '8px'}}>Searching {searchProvider}...</div>
                        </div>
                      )}
                      {!searchLoading && searchResults.length === 0 && !searchError && (
                        <div className="no-results-message">
                          <div className="no-results-icon">🔍</div>
                          <div className="no-results-text">
                            {searchMessage || 'No results yet. Use the form above or Advanced Search to find patent applications.'}
                          </div>
                        </div>
                      )}
                      {!searchLoading && searchResults.length > 0 && (
                        <>
                          <div className="search-results-toolbar">
                            <div className="toolbar-left">
                              <div>
                                <strong>{searchResults.length}</strong> result{searchResults.length === 1 ? '' : 's'}
                                {searchResults.length > searchResultsPerPage && (
                                  <span className="muted" style={{marginLeft: '0.5rem'}}>
                                    (Page {searchPage + 1} of {Math.ceil(searchResults.length / searchResultsPerPage)})
                                  </span>
                                )}
                              </div>
                              <div className="page-size-selector">
                                <label htmlFor="pageSize">Show:</label>
                                <select 
                                  id="pageSize"
                                  value={searchResultsPerPage} 
                                  onChange={(e) => {
                                    setSearchResultsPerPage(Number(e.target.value));
                                    setSearchPage(0);
                                  }}
                                >
                                  <option value={10}>10</option>
                                  <option value={25}>25</option>
                                  <option value={50}>50</option>
                                  <option value={100}>100</option>
                                  <option value={250}>250</option>
                                </select>
                              </div>
                              <div className="column-selector">
                                <button
                                  type="button"
                                  className="btn btn-small"
                                  onClick={() => setColumnSelectorOpen(!columnSelectorOpen)}
                                >
                                  Columns ({searchVisibleColumns.length})
                                </button>
                                {columnSelectorOpen && (
                                  <div className="column-selector-dropdown">
                                    {availableColumns.map(col => (
                                      <label key={col.key} className="column-option">
                                        <input
                                          type="checkbox"
                                          checked={searchVisibleColumns.includes(col.key)}
                                          onChange={() => toggleColumn(col.key)}
                                        />
                                        <span>{col.label}</span>
                                      </label>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="search-selection-controls">
                              <button
                                type="button"
                                className="btn btn-small"
                                onClick={handleSelectAllSearchResults}
                                disabled={searchResults.length === 0}
                              >
                                Select All
                              </button>
                              <button
                                type="button"
                                className="btn btn-small"
                                onClick={handleClearSearchSelection}
                                disabled={searchSelected.length === 0}
                              >
                                Clear Selection
                              </button>
                              <button
                                type="button"
                                className="btn btn-small btn-export"
                                onClick={handleExportToExcel}
                                disabled={searchResults.length === 0}
                              >
                                📊 Export to Excel
                              </button>
                            </div>
                          </div>

                          {/* Facet Results Display */}
                          {searchFacets && searchFacets.length > 0 && (
                            <div className="facet-results-section">
                              <h4>Facet Results</h4>
                              <div className="facet-results-grid">
                                {searchFacets.map((facetGroup, idx) => {
                                  const fieldName = Object.keys(facetGroup)[0];
                                  const facetValues = facetGroup[fieldName];
                                  
                                  if (!Array.isArray(facetValues) || facetValues.length === 0) return null;
                                  
                                  return (
                                    <div key={idx} className="facet-group">
                                      <h5>{fieldName}</h5>
                                      <ul className="facet-list">
                                        {facetValues.map((item, vIdx) => (
                                          <li key={vIdx} className="facet-item">
                                            <span className="facet-value">{item.value || 'N/A'}</span>
                                            <span className="facet-count">{item.count}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          <div className="search-results-table-container">
                            <table className="search-results-table">
                              <thead>
                                <tr>
                                  <th className="table-checkbox-col">
                                    <input
                                      type="checkbox"
                                      checked={searchResults.length > 0 && searchSelected.length === searchResults.length}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          handleSelectAllSearchResults();
                                        } else {
                                          handleClearSearchSelection();
                                        }
                                      }}
                                    />
                                  </th>
                                  {searchVisibleColumns.map(colKey => {
                                    const col = availableColumns.find(c => c.key === colKey);
                                    const isSortable = colKey !== 'pdf_link';
                                    const isSorted = sortColumn === colKey;
                                    return (
                                      <th 
                                        key={colKey}
                                        className={isSortable ? 'sortable' : ''}
                                        onClick={() => isSortable && handleSort(colKey)}
                                      >
                                        {col ? col.label : colKey}
                                        {isSorted && (
                                          <span className="sort-indicator">
                                            {sortDirection === 'asc' ? ' ▲' : ' ▼'}
                                          </span>
                                        )}
                                      </th>
                                    );
                                  })}
                                </tr>
                              </thead>
                              <tbody>
                                {sortedResults.slice(searchPage * searchResultsPerPage, (searchPage + 1) * searchResultsPerPage).map((result) => {
                                  const checked = searchSelected.includes(result.result_id);
                                  const alive = isApplicationAlive(result.status_code);
                                  
                                  // Check if this patent already exists in database
                                  const identifier = result.application_number || result.patent_number || result.publication_number;
                                  const alreadyExists = existingPatents.includes(identifier);
                                  
                                  // Only allow selection if alive and not already exists
                                  const isSelectable = alive && !alreadyExists;
                                  
                                  return (
                                    <tr
                                      key={result.result_id}
                                      className={`${checked ? 'selected' : ''} ${!alive ? 'application-dead' : ''} ${alreadyExists ? 'patent-exists' : ''}`}
                                      onClick={() => isSelectable && handleSearchSelect(result.result_id)}
                                      style={{ cursor: isSelectable ? 'pointer' : 'not-allowed' }}
                                    >
                                      <td className="table-checkbox-col">
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          disabled={!isSelectable}
                                          onChange={() => isSelectable && handleSearchSelect(result.result_id)}
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      </td>
                                      {searchVisibleColumns.map(colKey => {
                                        let value = result[colKey];
                                        
                                        // Special handling for certain fields
                                        if (colKey === 'pdf_link') {
                                          return (
                                            <td key={colKey}>
                                              {value ? (
                                                <a 
                                                  href={value} 
                                                  target="_blank" 
                                                  rel="noopener noreferrer"
                                                  onClick={(e) => e.stopPropagation()}
                                                  className="pdf-link"
                                                >
                                                  📄 View PDF
                                                </a>
                                              ) : '—'}
                                            </td>
                                          );
                                        }
                                        
                                        if (colKey === 'status') {
                                          return (
                                            <td key={colKey}>
                                              <span className={`status-badge status-${value || 'unknown'}`}>
                                                {value || '—'}
                                              </span>
                                            </td>
                                          );
                                        }
                                        
                                        if (colKey === 'status_code') {
                                          const statusDesc = result.status_code_description;
                                          return (
                                            <td key={colKey}>
                                              {value}{statusDesc ? ` - ${statusDesc}` : ''}
                                            </td>
                                          );
                                        }
                                        
                                        if (colKey === 'art_unit') {
                                          const artUnitDesc = result.art_unit_description;
                                          return (
                                            <td key={colKey}>
                                              {value}{artUnitDesc ? ` - ${artUnitDesc}` : ''}
                                            </td>
                                          );
                                        }
                                        
                                        return (
                                          <td key={colKey}>
                                            {value != null ? value : '—'}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>

                          {searchResults.length > searchResultsPerPage && (
                            <div className="search-pagination">
                              <button
                                className="btn btn-small"
                                onClick={() => setSearchPage(p => Math.max(0, p - 1))}
                                disabled={searchPage === 0}
                              >
                                ← Previous
                              </button>
                              <span className="pagination-info">
                                Page {searchPage + 1} of {Math.ceil(searchResults.length / searchResultsPerPage)}
                              </span>
                              <button
                                className="btn btn-small"
                                onClick={() => setSearchPage(p => Math.min(Math.ceil(searchResults.length / searchResultsPerPage) - 1, p + 1))}
                                disabled={searchPage >= Math.ceil(searchResults.length / searchResultsPerPage) - 1}
                              >
                                Next →
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {(ptoMessage || ptoError) && (
              <div className="form-section">
                {ptoMessage && <div className="inline-success">{ptoMessage}</div>}
                {ptoError && <div className="inline-error">{ptoError}</div>}
              </div>
            )}
          </section>

          {/* Download Progress */}
          <section className="card pipeline-card">
            <header>
              <div>
                <h2>Download Status</h2>
                <p>Real-time progress of PTO data acquisition</p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn btn-secondary" onClick={refreshPtoStatus}>
                  Refresh
                </button>
              </div>
            </header>

            <div className="status-section">
              <div className={`status-badge status-${ptoStatus.status}`}>
                {ptoStatus.status.toUpperCase()}
              </div>

                {ptoStatus.current && (
                  <div className="status-message">{ptoStatus.current}</div>
                )}

                {/* Error Details - Show prominently */}
                {ptoStatus.error_details && (
                  <div className="error-details-inline">
                    <strong>⚠️ {ptoStatus.error_details.type}:</strong> {ptoStatus.error_details.message}
                  </div>
                )}

                {/* Status Log - Always visible */}
                <div className="status-log-section">
                  <h3>Status Log</h3>
                  <div className="status-log">
                    {ptoStatus.log && ptoStatus.log.length > 0 ? (
                      ptoStatus.log.slice(0, 8).map((entry, idx) => (
                        <div key={idx} className="log-entry">
                          {entry}
                        </div>
                      ))
                    ) : (
                      <div className="log-entry muted">No activity yet</div>
                    )}
                  </div>
                </div>

                {ptoStatus.total > 0 && (
                  <div className="progress-section">
                    <div className="progress-stats">
                      <span>Progress: {ptoStatus.progress} / {ptoStatus.total}</span>
                      <span>{Math.round((ptoStatus.progress / ptoStatus.total) * 100)}%</span>
                    </div>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${(ptoStatus.progress / ptoStatus.total) * 100}%` }}
                      />
                    </div>
                  </div>
                )}

                {ptoStatus.results && (
                  <div className="results-summary">
                    <h3>Download Results</h3>
                    <div className="results-grid">
                      <div>
                        <span>Total Requested</span>
                        <strong>{ptoStatus.results.total}</strong>
                      </div>
                      <div>
                        <span>Successful</span>
                        <strong className="success">{ptoStatus.results.successful}</strong>
                      </div>
                      <div>
                        <span>Failed</span>
                        <strong className="error">{ptoStatus.results.failed}</strong>
                      </div>
                    </div>
                  </div>
                )}

                {/* Failed Patents List */}
                {ptoStatus.failed_patents && ptoStatus.failed_patents.length > 0 && (
                  <div className="failed-patents">
                    <h3>Failed Patents</h3>
                    <div className="failed-list">
                      {ptoStatus.failed_patents.map((failure, idx) => (
                        <div key={idx} className="failed-item">
                          <span className="patent-number">{failure.patent_number}</span>
                          <span className="error-reason">{failure.error}</span>
                        </div>
                      ))}
                      {ptoStatus.results?.failed > ptoStatus.failed_patents.length && (
                        <div className="failed-item muted">
                          ... and {ptoStatus.results.failed - ptoStatus.failed_patents.length} more failures
                        </div>
                      )}
                    </div>
                  </div>
                )}
            </div>
          </section>
        </div>
      )}

      {/* TAB 2: PROCESSING */}
      {activeTab === 'processing' && (
        <div className="tab-content">
          <section className="card pipeline-card">
            <header>
              <div>
                <h2>Process Patents Through Pipeline</h2>
                <p>Select consecutive phases to process data in the database. Each phase requires the previous phase to be completed.</p>
              </div>
            </header>

            {processingStatus?.database_status && (
              <div className="db-status-summary">
                <h3>Database Content</h3>
                <div className="db-status-grid">
                  <div>
                    <span>Raw Patents</span>
                    <strong>{processingStatus.database_status.raw_patents || 0}</strong>
                  </div>
                  <div>
                    <span>Embeddings</span>
                    <strong>{processingStatus.database_status.embeddings || 0}</strong>
                  </div>
                  <div>
                    <span>Claim Analysis</span>
                    <strong>{processingStatus.database_status.claim_analysis || 0}</strong>
                  </div>
                  <div>
                    <span>Digests</span>
                    <strong>{processingStatus.database_status.digests || 0}</strong>
                  </div>
                  <div>
                    <span>Labels</span>
                    <strong>{processingStatus.database_status.labels || 0}</strong>
                  </div>
                  <div>
                    <span>Drafts</span>
                    <strong>{processingStatus.database_status.drafts || 0}</strong>
                  </div>
                  <div>
                    <span>Catalogue</span>
                    <strong>{processingStatus.database_status.catalogue || 0}</strong>
                  </div>
                </div>
              </div>
            )}

            <div className="form-section">
              <div className="form-row">
                <label>Select Phases (must be consecutive)</label>
                <div className="phase-sequence">
                  {SELECTABLE_PHASES.map((phase, idx) => {
                    const isSelected = selectedPhases.includes(phase.id);
                    const phaseState = getPhaseState(phase.id);
                    const buttonDisabled = !isSelected && !phaseState.enabled;
                    const phaseReady = readyCounts[phase.id] || 0;
                    const willRunNext = selectionBounds && phase.id === selectionBounds.max + 1 && phaseReady === 0;
                    const readyLabel = `${phaseReady} ready`;
                    const metaText = phaseReady > 0
                      ? 'Ready to run'
                      : willRunNext
                        ? 'Will run after previous phase'
                        : 'No pending work';
                    const connectorActive = selectedPhases.includes(phase.id) && selectedPhases.includes(phase.id + 1);

                    return (
                      <div key={phase.id} className="phase-sequence-item">
                        <button
                          type="button"
                          className={`phase-node ${isSelected ? 'selected' : ''} ${buttonDisabled ? 'disabled' : ''}`}
                          onClick={() => handlePhaseToggle(phase.id)}
                          disabled={buttonDisabled}
                          title={phaseState.reason}
                        >
                          <span className="phase-node-number">{phase.id}</span>
                          <div className="phase-node-body">
                            <span className="phase-node-title">
                              {phase.name}
                              <span className={`phase-node-count ${phaseReady > 0 ? 'has-work' : ''}`}>
                                {readyLabel}
                              </span>
                            </span>
                            <span className="phase-node-meta">{metaText}</span>
                          </div>
                        </button>
                        {idx < SELECTABLE_PHASES.length - 1 && (
                          <span className={`phase-connector ${connectorActive ? 'active' : ''}`}></span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {selectedPhases.length === 0 && availablePhaseStarts.length === 0 && processingStatus && (
                  <div className="hint">No phases currently have pending work.</div>
                )}
                {selectedPhases.length > 0 && (
                  <div className="selection-info">
                    Selected run: Phases {Math.min(...selectedPhases)} - {Math.max(...selectedPhases)}
                  </div>
                )}
              </div>

              <button
                className="btn btn-primary"
                onClick={handleRunProcessing}
                disabled={selectedPhases.length === 0 || processing}
              >
                {processing ? 'Processing...' : `Run Selected Phases (${selectedPhases.length})`}
              </button>

              {processingMessage && <div className="inline-success">{processingMessage}</div>}
              {processingError && <div className="inline-error">{processingError}</div>}
            </div>
          </section>

          {/* Processing Progress */}
          {runStatus && (
            <section className="card pipeline-card">
              <header>
                <div>
                  <h2>Processing Progress</h2>
                  <p>Live status, logs, and outcomes for the latest pipeline run</p>
                </div>
                <button className="btn btn-secondary" onClick={refreshProcessingStatus}>
                  Refresh
                </button>
              </header>

              <div className="status-section">
                <div className={`status-badge status-${runStatus.status || 'idle'}`}>
                  {(runStatus.status || 'idle').toUpperCase()}
                </div>

                {runStatus.current && (
                  <div className="status-message">{runStatus.current}</div>
                )}

                {runStatus.active_phases && runStatus.active_phases.length > 0 && (
                  <div className="run-meta">
                    Active phases:{' '}
                    <strong>{runStatus.active_phases.join(' -> ')}</strong>
                  </div>
                )}

                {typeof runStatus.batch_mode === 'boolean' && (
                  <div className="run-meta">
                    Mode:{' '}
                    <strong>{runStatus.batch_mode ? 'OpenAI Batch API' : 'Synchronous'}</strong>
                  </div>
                )}

                <div className="run-timing">
                  <span>Started: {formatTimestamp(runStatus.started_at) || 'Not started yet'}</span>
                  <span>
                    {runStatus.ended_at
                      ? `Ended: ${formatTimestamp(runStatus.ended_at)}`
                      : runStatus.status === 'running'
                        ? 'In progress'
                        : runStatus.status === 'queued'
                          ? 'Queued for execution'
                          : 'Waiting for next run'}
                  </span>
                </div>

                {runStatus.error_details && (
                  <div className="error-details-inline">
                    <strong>⚠️ {runStatus.error_details.type}:</strong> {runStatus.error_details.message}
                  </div>
                )}

                <div className="status-log-section">
                  <h3>Status Log</h3>
                  <div className="status-log">
                    {runStatus.log && runStatus.log.length > 0 ? (
                      runStatus.log.slice(0, 8).map((entry, idx) => (
                        <div key={idx} className="log-entry">
                          {entry}
                        </div>
                      ))
                    ) : (
                      <div className="log-entry muted">No activity yet</div>
                    )}
                  </div>
                </div>

                {batchJobs.length > 0 && (
                  <div className="batch-section">
                    <h3>Batch Jobs</h3>
                    <div className="batch-grid">
                      {batchJobs.map((batch) => {
                        const statusKey = (batch.status || 'queued').toLowerCase();
                        const completed = batch.completed ?? 0;
                        const total = batch.total ?? 0;
                        const failed = batch.failed ?? 0;
                        return (
                          <div key={batch.batch_id} className="batch-card">
                            <div className="batch-card-header">
                              <span className="batch-phase">{batch.phase || 'Batch'}</span>
                              <span className={`batch-status-pill status-${statusKey}`}>
                                {statusKey.toUpperCase()}
                              </span>
                            </div>
                            <div className="batch-card-body">
                              <div>
                                <span className="batch-label">Batch ID</span>
                                <span className="batch-value mono">{batch.batch_id}</span>
                              </div>
                              <div>
                                <span className="batch-label">Progress</span>
                                <span className="batch-value">{completed} / {total}</span>
                              </div>
                              <div>
                                <span className="batch-label">Failed</span>
                                <span className={`batch-value ${failed > 0 ? 'error' : ''}`}>{failed}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {progressInfo.total > 0 && (
                  <div className="progress-section">
                    <div className="progress-stats">
                      <span>
                        Progress: {progressInfo.completed} / {progressInfo.total}
                      </span>
                      <span>{progressInfo.percent}%</span>
                    </div>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${progressInfo.percent}%` }}
                      />
                    </div>
                  </div>
                )}

                {phaseBreakdown.length > 0 && (
                  <div className="results-summary">
                    <h3>Phase Breakdown</h3>
                    <div className="results-grid results-grid--compact">
                      {phaseBreakdown.map(({ phaseId, total, completed }) => (
                        <div key={phaseId}>
                          <span>{PHASE_NAMES[phaseId] || `Phase ${phaseId}`}</span>
                          <strong>
                            {completed} / {total}
                          </strong>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {runStatus.results?.phase_results && (
                  <div className="results-summary">
                    <h3>Phase Outcomes</h3>
                    <div className="results-grid results-grid--compact">
                      {Object.entries(runStatus.results.phase_results).map(([phaseId, result]) => (
                        <div key={phaseId}>
                          <span>{PHASE_NAMES[Number(phaseId)] || `Phase ${phaseId}`}</span>
                          <strong className={result.success ? 'success' : 'error'}>
                            {result.success ? `${result.patents_processed || 0} processed` : 'Failed'}
                          </strong>
                          {!result.success && result.error && (
                            <span className="error-reason">{result.error}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      )}

      {/* TAB 3: DATABASE MANAGEMENT */}
      {activeTab === 'database' && (
        <div className="tab-content">
          <section className="card pipeline-card">
            <header>
              <div>
                <h2>Database Management</h2>
                <p>View, edit, and manage all database tables</p>
              </div>
              <button className="btn btn-secondary" onClick={refreshDbStats}>
                Refresh Stats
              </button>
            </header>

            {dbLoading ? (
              <div className="loading-state">Loading database information...</div>
            ) : dbStats ? (
              <>
                <div className="db-stats-summary">
                  <h3>Table Statistics</h3>
                  <div className="table-stats-grid">
                    {Object.entries(dbStats.tables || {}).map(([table, count]) => (
                      <div key={table} className="table-stat">
                        <span className="table-name">{table}</span>
                        <strong className="table-count">{count.toLocaleString()}</strong>
                      </div>
                    ))}
                  </div>
                </div>

                <DatabaseInspector />
              </>
            ) : (
              <div className="empty-state">No database statistics available</div>
            )}
          </section>
        </div>
      )}

      {/* Advanced Search Modal */}
      <AdvancedSearchModal
        isOpen={advancedSearchOpen}
        onClose={() => setAdvancedSearchOpen(false)}
        onApply={handleAdvancedSearchApply}
      />
    </div>
  );
}

export default Pipeline;
